import PropTypes from 'prop-types';
import { useFieldArray } from 'react-hook-form';
import { ArrowDown, ArrowUp, CornerDownRight } from 'lucide-react';
import { PiPlusBold, PiTrashBold } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import {
  Button,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import { Input } from '#/modules/commonComponents/form/Input';
import { getNestedError } from '#/modules/commonComponents/form/formFieldUtils';
import {
  createQuotationLine,
  createQuotationSubLine,
} from '#/modules/crmMgmt/quotation/helper/quotationLines';

const CELL_INPUT_CLASS = 'h-9 min-h-9 w-full';
const COLUMN_COUNT = 9;

function CellInput({ register, name, label, errors, type = 'text', placeholder }) {
  const error = getNestedError(errors, name);
  return (
    <>
      <Input
        type={type}
        min={type === 'number' ? 0 : undefined}
        step={type === 'number' ? 'any' : undefined}
        placeholder={placeholder}
        aria-label={label}
        aria-invalid={error ? true : undefined}
        title={error?.message || undefined}
        className={cn(CELL_INPUT_CLASS, error && 'form_error')}
        {...register(name)}
      />
      {error ? <p className="mt-0.5 text-xs text-destructive">{error.message}</p> : null}
    </>
  );
}

CellInput.propTypes = {
  register: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  errors: PropTypes.object,
  type: PropTypes.string,
  placeholder: PropTypes.string,
};

function MoveRemoveButtons({ label, index, count, onMove, onRemove }) {
  return (
    <div className="flex items-center justify-center gap-1">
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={`上移${label}`}
        disabled={index === 0}
        onClick={() => onMove(index, index - 1)}
      >
        <ArrowUp className="size-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={`下移${label}`}
        disabled={index === count - 1}
        onClick={() => onMove(index, index + 1)}
      >
        <ArrowDown className="size-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
        aria-label={`移除${label}`}
        onClick={() => onRemove(index)}
      >
        <PiTrashBold className="size-4" />
      </Button>
    </div>
  );
}

MoveRemoveButtons.propTypes = {
  label: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  count: PropTypes.number.isRequired,
  onMove: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

/** 一個主項目列＋其子項目列（子項目不計價，無單價欄）。 */
function QuotationLineRows({ control, register, errors, lineIndex, lineCount, onMoveLine, onRemoveLine, isSaved }) {
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: `lines.${lineIndex}.subLines`,
    keyName: 'fieldKey',
  });
  const base = `lines.${lineIndex}`;
  const no = lineIndex + 1;

  return (
    <>
      <tr className="align-top">
        <td className="px-2 py-2 text-center tabular-nums text-gray-600">
          {no}
          {isSaved ? null : <p className="text-[10px] text-primary-600">未儲存</p>}
        </td>
        <td className="px-2 py-2">
          <CellInput register={register} errors={errors} name={`${base}.productNo`} label={`第 ${no} 項料號`} />
        </td>
        <td className="px-2 py-2">
          <CellInput register={register} errors={errors} name={`${base}.itemName`} label={`第 ${no} 項品名`} />
        </td>
        <td className="px-2 py-2">
          <CellInput register={register} errors={errors} name={`${base}.qty`} label={`第 ${no} 項數量`} type="number" />
        </td>
        <td className="px-2 py-2">
          <CellInput register={register} errors={errors} name={`${base}.unitName`} label={`第 ${no} 項單位`} />
        </td>
        <td className="px-2 py-2">
          <CellInput
            register={register}
            errors={errors}
            name={`${base}.unitPrice`}
            label={`第 ${no} 項單價`}
            type="number"
          />
        </td>
        <td className="px-2 py-2">
          <CellInput
            register={register}
            errors={errors}
            name={`${base}.customerRemark`}
            label={`第 ${no} 項客戶備註`}
          />
        </td>
        <td className="px-2 py-2">
          <CellInput
            register={register}
            errors={errors}
            name={`${base}.internalRemark`}
            label={`第 ${no} 項內部備註`}
          />
        </td>
        <td className="px-2 py-2">
          <MoveRemoveButtons
            label={`第 ${no} 項`}
            index={lineIndex}
            count={lineCount}
            onMove={onMoveLine}
            onRemove={onRemoveLine}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-1 w-full text-xs"
            onClick={() => append(createQuotationSubLine())}
          >
            <PiPlusBold className="size-3" />
            子項目
          </Button>
        </td>
      </tr>
      {fields.map((field, subIndex) => {
        const subBase = `${base}.subLines.${subIndex}`;
        const label = `第 ${no} 項子項目 ${subIndex + 1}`;
        return (
          <tr key={field.fieldKey} className="align-top bg-gray-50/70">
            <td className="px-2 py-2 text-right text-gray-400">
              <CornerDownRight className="ml-auto size-4" aria-hidden />
            </td>
            <td className="px-2 py-2">
              <CellInput register={register} errors={errors} name={`${subBase}.productNo`} label={`${label}料號`} />
            </td>
            <td className="px-2 py-2">
              <CellInput register={register} errors={errors} name={`${subBase}.itemName`} label={`${label}品名`} />
            </td>
            <td className="px-2 py-2">
              <CellInput
                register={register}
                errors={errors}
                name={`${subBase}.qty`}
                label={`${label}數量`}
                type="number"
              />
            </td>
            <td className="px-2 py-2">
              <CellInput register={register} errors={errors} name={`${subBase}.unitName`} label={`${label}單位`} />
            </td>
            <td className="px-2 py-2 text-xs text-muted-foreground">不計價</td>
            <td className="px-2 py-2">
              <CellInput
                register={register}
                errors={errors}
                name={`${subBase}.customerRemark`}
                label={`${label}客戶備註`}
              />
            </td>
            <td className="px-2 py-2">
              <CellInput
                register={register}
                errors={errors}
                name={`${subBase}.internalRemark`}
                label={`${label}內部備註`}
              />
            </td>
            <td className="px-2 py-2">
              <MoveRemoveButtons
                label={label}
                index={subIndex}
                count={fields.length}
                onMove={move}
                onRemove={remove}
              />
            </td>
          </tr>
        );
      })}
    </>
  );
}

QuotationLineRows.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  lineIndex: PropTypes.number.isRequired,
  lineCount: PropTypes.number.isRequired,
  onMoveLine: PropTypes.func.isRequired,
  onRemoveLine: PropTypes.func.isRequired,
  isSaved: PropTypes.bool,
};

/**
 * 手動報價項目編輯：主項目（料號、品名、數量、單位、單價、客戶／內部備註）＋縮排子項目（不計價）。
 * 草稿可不完整；金額、稅金、總額由後端計算，儲存後於詳情顯示。
 */
const QuotationLinesEditor = ({ control, register, errors }) => {
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'lines',
    keyName: 'fieldKey',
  });

  return (
    <section className="@container/form-section pb-5">
      <div className="form-section__header flex-wrap gap-2">
        <h2 className="form-block__title mb-0!">報價項目</h2>
        <Button type="button" size="sm" onClick={() => append(createQuotationLine())}>
          <PiPlusBold className="size-3.5" />
          新增主項目
        </Button>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        主項目的品名、數量、單位、單價都填齊才計入金額；子項目只做縮排說明、不計價，品名必填。
        數量須大於 0、單價不可為負，最多四位小數。小計、5% 稅金與取整後總額由系統計算，儲存後顯示。
        物料／BOM 成本估算請改用「成本試算」。
      </p>

      <div className="mt-3 overflow-x-auto rounded border border-gray-400/65">
        <table className="w-full min-w-[72rem] text-left text-sm">
          <thead className="bg-gray-100 text-xs text-gray-600">
            <tr>
              <th className="w-12 px-2 py-2 text-center font-medium">#</th>
              <th className="w-32 px-2 py-2 font-medium">料號</th>
              <th className="px-2 py-2 font-medium">品名</th>
              <th className="w-28 px-2 py-2 font-medium">數量</th>
              <th className="w-24 px-2 py-2 font-medium">單位</th>
              <th className="w-32 px-2 py-2 font-medium">單價</th>
              <th className="w-44 px-2 py-2 font-medium">客戶備註</th>
              <th className="w-44 px-2 py-2 font-medium">內部備註</th>
              <th className="w-32 px-2 py-2 text-center font-medium">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-dashed divide-gray-400/65">
            {fields.length === 0 ? (
              <tr>
                <td colSpan={COLUMN_COUNT} className="px-3 py-8 text-sm text-muted-foreground">
                  尚無項目，可先存成空草稿。
                </td>
              </tr>
            ) : fields.map((field, index) => (
              <QuotationLineRows
                key={field.fieldKey}
                control={control}
                register={register}
                errors={errors}
                lineIndex={index}
                lineCount={fields.length}
                onMoveLine={move}
                onRemoveLine={remove}
                isSaved={Boolean(field.lineId)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

QuotationLinesEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
};

export default QuotationLinesEditor;
